import { TypedDocumentNode, gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import Shine from '@sorare/core/src/atoms/ui/Shine';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { isType } from '@sorare/core/src/gql';
import { CARD_BORDER_RADIUS } from '@sorare/core/src/lib/cards';
import { draft, glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { stroke } from '@sorare/core/src/style/utils';

import { Bonus } from '@sorare/marketplace/src/components/card/USSports/Bonus';

import { InjuryStatusIcon } from '@sorare/us-sports/src/components/player/PlayerInjury/InjuryStatusIcon';

import {
  CardSelection_NBACard,
  CardSelection_NBAPlayer,
} from './__generated__/index.graphql';

const CARD_WIDTH = 200;
const LARGER_MOBILE_CARD_WIDTH = 220;
const LARGER_DESKTOP_CARD_WIDTH = 260;

const Wrapper = styled(Vertical).attrs({ center: true })`
  justify-items: center;
  &.dismissed {
    opacity: 0.5;
  }
`;

const PickCardButton = styled(ButtonBase)`
  transition: transform 0.1s ease-out;
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: ${CARD_BORDER_RADIUS};
  overflow: hidden;
  cursor: unset;

  max-width: ${CARD_WIDTH}px;
  &.larger {
    max-width: ${LARGER_MOBILE_CARD_WIDTH}px;
    @media ${tabletAndAbove} {
      max-width: ${LARGER_DESKTOP_CARD_WIDTH}px;
    }
  }

  &.selectable {
    overflow: visible;
    cursor: pointer;
    &:hover,
    &:focus {
      opacity: 1;
      transform: scale(1.01);
    }
  }
  &.selected img {
    ${stroke()}
  }
`;

const CardOption = styled.img`
  width: 100%;
`;

const Details = styled(Horizontal).attrs({ gap: 2 })`
  font-size: 12px;
`;

type Props = {
  card: CardSelection_NBACard;
  larger?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  isDismissed?: boolean;
  onSelect?: () => any;
  onDisplayPlayerInfo?: () => any;
  shine?: boolean;
};

export const CardSelection = ({
  card,
  larger = false,
  isSelectable = true,
  isSelected,
  isDismissed,
  onDisplayPlayerInfo,
  onSelect,
  shine = false,
}: Props) => {
  const { formatMessage } = useIntl();
  const buttonProps = isSelectable ? { tabIndex: 0 } : { tabIndex: -1 };

  const cardImageUrl = proxyUrl(card.pictureUrl!, {
    cropWidth: 320,
  });
  const { anyPlayer } = card;
  if (!isType(anyPlayer, 'NBAPlayer')) return null;
  return (
    <Wrapper
      className={classNames({
        dismissed: isDismissed,
      })}
    >
      <PickCardButton
        {...buttonProps}
        className={classNames({
          larger,
          selectable: isSelectable,
          selected: isSelected,
          dismissed: isDismissed,
        })}
        {...buttonProps}
        type="button"
        title={
          isSelectable
            ? formatMessage(
                {
                  id: 'CardSwap.CardSelection.title',
                  defaultMessage: 'Pick {name}',
                },
                { name: anyPlayer.displayName }
              )
            : anyPlayer.displayName
        }
        onClick={onSelect}
      >
        {shine ? (
          <Shine>
            <CardOption src={cardImageUrl} alt="" />
          </Shine>
        ) : (
          <CardOption src={cardImageUrl} alt="" />
        )}
      </PickCardButton>
      <Details>
        <Cost
          score={anyPlayer.tenGameAverageGameStats.score}
          title={formatMessage(draft.nbaTenGameAverageScoreTitle, {
            score: anyPlayer.tenGameAverageGameStats.score,
          })}
        />{' '}
        <Bonus
          seasonBonus={card.seasonBonus}
          rarityBonus={card.rarityBonus}
          xpBonus={card.xpBonus}
          collectionBonus={card.collectionBonus}
          totalBonus={card.totalBonus}
        />
        <Horizontal>
          {onDisplayPlayerInfo && (
            <IconButton
              disableDebounce
              small
              color="quaternary"
              onClick={e => {
                e.stopPropagation();
                onDisplayPlayerInfo();
              }}
              title={formatMessage(glossary.showPlayerInfo)}
              icon={faInfoCircle}
            />
          )}
          {(!anyPlayer.isActive || anyPlayer.playerInjury) && (
            <InjuryStatusIcon />
          )}
        </Horizontal>
      </Details>
    </Wrapper>
  );
};

const PLAYER_FRAGMENT = gql`
  fragment CardSelection_NBAPlayer on NBAPlayer {
    id
    slug
    displayName
    isActive
    playerInjury {
      status
    }
    tenGameAverageGameStats {
      score
    }
  }
` as TypedDocumentNode<CardSelection_NBAPlayer>;

CardSelection.fragments = {
  NBACard: gql`
    fragment CardSelection_NBACard on NBACard {
      id
      slug
      pictureUrl
      seasonBonus
      rarityBonus
      xpBonus
      collectionBonus
      totalBonus
      anyPlayer {
        slug
        ...CardSelection_NBAPlayer
      }
    }
    ${PLAYER_FRAGMENT}
  ` as TypedDocumentNode<CardSelection_NBACard>,
};
